import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import vehicleDocumentsService from "../../services/vehicle_document_service";
import { getCurrentUser } from "../../services/auth_service";
import {
  FiSearch, FiPlus, FiX, FiEdit2, FiAlertCircle,
  FiRefreshCw, FiTruck, FiAlertTriangle, FiClock, FiFileText,
  FiDownload, FiPrinter, FiTrash2, FiChevronDown,
} from "react-icons/fi";
import "./VehicleDocumentsList.css";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import {
  isSuperadmin,
  hasPermission,
} from "../utils/permissions";
const DOC_LABELS = {
  rc: "Registration Certificate",
  permit: "Permit",
  fitness: "Fitness Certificate",
  insurance: "Insurance",
  tax: "Road Tax",
  pollution: "Pollution (PUC)",
  police_sticker: "Police Sticker",
  city_police_permit: "City Police Permit",
};
const DOC_KEYS = Object.keys(DOC_LABELS);
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?.*)?$/i;
const PDF_EXT_RE = /\.(pdf)(\?.*)?$/i;
const isImageFile = (url) => IMAGE_EXT_RE.test(url || "");
const isPdfFile = (url) => PDF_EXT_RE.test(url || "");
const rasterizeImageToPng = async (bytes) => {
  const blob = new Blob([bytes]);
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0);
  const pngBlob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas encode failed"))), "image/png")
  );
  return new Uint8Array(await pngBlob.arrayBuffer());
};
const formatServerDateTime = (value) => {
  if (!value) return "-";
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
  const iso = hasOffset ? value : `${value}Z`;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });
};
const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const resolveUserName = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    return value.name || value.full_name || value.username || value.email || "-";
  }
  return String(value);
};
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 40;
const GRID_COLS = 2;
const GRID_ROWS = 2;
const GRID_GAP = 14;
const GRID_LABEL_H = 14;
const ACTIONS_MENU_WIDTH = 138;
const ACTIONS_MENU_HEIGHT = 90;
const PAGE_SIZE = 20;
const TOAST_AUTO_DISMISS_MS = 3200;
export default function VehicleDocumentsList() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser() || {};
  const confirm = useConfirm();
  const isSuperadminUser = isSuperadmin();
  const canViewDocuments = true;
  const canEditDocuments =
    isSuperadminUser || hasPermission("vehicle_documents", "edit");
  const canDeleteDocuments =
    isSuperadminUser || hasPermission("vehicle_documents", "delete");
  const hasDocumentAccess = true;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [thumbUrls, setThumbUrls] = useState({});
  const [openActionRowId, setOpenActionRowId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef(null);
  const actionButtonRefs = useRef({});
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = (type, text) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, text });
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
  };
  const dismissToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  };
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);
  useEffect(() => {
    if (openActionRowId === null) return;
    const handleClickOutside = (e) => {
      const btn = actionButtonRefs.current[openActionRowId];
      if (
        actionMenuRef.current && !actionMenuRef.current.contains(e.target) &&
        btn && !btn.contains(e.target)
      ) {
        setOpenActionRowId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionRowId]);
  useEffect(() => {
    if (openActionRowId === null) return;
    const reposition = () => {
      const btn = actionButtonRefs.current[openActionRowId];
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      let left = rect.right - ACTIONS_MENU_WIDTH;
      let top = rect.bottom + 6;
      if (top + ACTIONS_MENU_HEIGHT > window.innerHeight) {
        top = rect.top - 6 - ACTIONS_MENU_HEIGHT;
      }
      left = Math.max(8, Math.min(left, window.innerWidth - ACTIONS_MENU_WIDTH - 8));
      setMenuPos({ top, left });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [openActionRowId]);
  const openActionsMenu = (rowId) => {
    if (openActionRowId === rowId) {
      setOpenActionRowId(null);
      return;
    }
    const btn = actionButtonRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      let left = rect.right - ACTIONS_MENU_WIDTH;
      let top = rect.bottom + 6;
      if (top + ACTIONS_MENU_HEIGHT > window.innerHeight) {
        top = rect.top - 6 - ACTIONS_MENU_HEIGHT;
      }
      left = Math.max(8, Math.min(left, window.innerWidth - ACTIONS_MENU_WIDTH - 8));
      setMenuPos({ top, left });
    }
    setOpenActionRowId(rowId);
  };
  const loadRows = async (search) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const data = await vehicleDocumentsService.getAllVehicleDocuments({
        search,
        status: "active",
      });
      setRows(data || []);
    } catch (err) {
      setErrorMsg("Couldn't load vehicle documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadRows(searchTerm || undefined); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadRows(searchTerm || undefined), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => {
    setPage(1);
  }, [rows.length, searchTerm, statusFilter]);
  const stats = useMemo(() => {
    const total = rows.length;
    const withExpired = rows.filter((r) => r.expired_count > 0).length;
    const withExpiringSoon = rows.filter((r) => r.expired_count === 0 && r.expiring_soon_count > 0).length;
    const clean = total - withExpired - withExpiringSoon;
    return { total, withExpired, withExpiringSoon, clean };
  }, [rows]);
  const filteredRows = useMemo(() => {
    if (statusFilter === "expired") {
      return rows.filter((r) => r.expired_count > 0);
    }
    if (statusFilter === "soon") {
      return rows.filter(
        (r) => r.expired_count === 0 && r.expiring_soon_count > 0
      );
    }
    if (statusFilter === "clear") {
      return rows.filter(
        (r) => r.expired_count === 0 && r.expiring_soon_count === 0
      );
    }
    return rows;
  }, [rows, statusFilter]);
  const totalPages = Math.max(Math.ceil(filteredRows.length / PAGE_SIZE), 1);
  const pageStart = filteredRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, filteredRows.length);
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const handleStatusCardClick = (filter) => {
    setStatusFilter((current) => (current === filter ? "all" : filter));
    setPage(1);
  };
  const goToAddPage = () => {
    if (!canEditDocuments) return;
    navigate("/add-vehicle-documents");
  };
  const goToEditPage = (row) => {
    if (!canEditDocuments) return;
    navigate(`/add-vehicle-documents/${row.vehicle_id}`);
  };
  const handleDeleteRow = async (row, e) => {
    e.stopPropagation();
    if (!canDeleteDocuments) return;
    const confirmed = await confirm({
      title: "Delete Vehicle Documents",
      message: `Delete documents for ${row.vehicle_registration_number}? This can be restored later.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
    });
    if (!confirmed) return;
    setDeletingId(row.id);
    try {
      await vehicleDocumentsService.deleteVehicleDocuments(row.vehicle_id);
      showToast("success", "Vehicle documents deleted successfully");
      await loadRows(searchTerm || undefined);
    } catch (err) {
      showToast("error", "Delete failed. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };
  const openView = async (row) => {
    setViewRow(row);
    setViewDoc(null);
    setViewError("");
    setViewLoading(true);
    try {
      const full = await vehicleDocumentsService.getVehicleDocuments(row.vehicle_id);
      setViewDoc(full);
    } catch (err) {
      setViewError("Couldn't load full document details.");
    } finally {
      setViewLoading(false);
    }
  };
  const closeView = () => {
    setViewRow(null);
    setViewDoc(null);
    setViewError("");
  };
  const openDocPreview = async (url, label) => {
    if (!url) return;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const resolvedUrl = vehicleDocumentsService.resolveFileUrl(url);
      const kind = isImageFile(resolvedUrl) ? "image" : isPdfFile(resolvedUrl) ? "pdf" : "other";
      if (kind === "other") {
        setPreviewDoc({ url: null, label, kind });
        return;
      }
      const { blobUrl } = await vehicleDocumentsService.previewFile(url);
      setPreviewDoc({ url: blobUrl, label, kind });
    } catch (err) {
      console.error("Preview failed:", err);
      setPreviewError("Couldn't load this document. Please try downloading it.");
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closeDocPreview = () => {
    if (previewDoc?.url && previewDoc.url.startsWith("blob:")) {
      URL.revokeObjectURL(previewDoc.url);
    }
    setPreviewDoc(null);
    setPreviewError("");
    setPreviewLoading(false);
  };
  useEffect(() => {
    let cancelled = false;
    let createdUrls = [];
    const loadThumbnails = async () => {
      if (!viewDoc) {
        setThumbUrls({});
        return;
      }
      const nextUrls = {};
      for (const key of DOC_KEYS) {
        const fileUrl = viewDoc?.[`${key}_file_url`];
        if (!fileUrl) continue;
        const resolvedUrl = vehicleDocumentsService.resolveFileUrl(fileUrl);
        if (!isImageFile(resolvedUrl)) continue;
        try {
          const { blobUrl } = await vehicleDocumentsService.previewFile(fileUrl);
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          createdUrls.push(blobUrl);
          nextUrls[key] = blobUrl;
          setThumbUrls({ ...nextUrls });
        } catch (err) {
          if (cancelled) return;
        }
      }
    };
    loadThumbnails();
    return () => {
      cancelled = true;
      createdUrls.forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
      setThumbUrls({});
    };
  }, [viewDoc]);
  const handleDownload = async (url, filename, key) => {
    if (!url) return;
    setDownloadingKey(key || url);
    try {
      const { blob } = await vehicleDocumentsService.downloadFile(url, filename);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      setViewError("Couldn't download this document. Please try again.");
    } finally {
      setDownloadingKey(null);
    }
  };
  const buildCombinedPdf = async () => {
    const pdfDoc = await PDFDocument.create();
    const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const navy = rgb(0.09, 0.23, 0.41);
    const blue = rgb(0.15, 0.39, 0.72);
    const ink = rgb(0.19, 0.20, 0.23);
    const muted = rgb(0.44, 0.47, 0.52);
    const lightCard = rgb(0.975, 0.985, 0.996);
    const border = rgb(0.88, 0.91, 0.95);
    const white = rgb(1, 1, 1);
    const drawHeader = (page, title, subtitle = "") => {
      // Compact, clean navy document heading for both Print and Download PDFs.
      page.drawRectangle({
        x: 0,
        y: PAGE_H - 62,
        width: PAGE_W,
        height: 62,
        color: navy,
      });
      page.drawText(title || "Vehicle Documents", {
        x: MARGIN,
        y: PAGE_H - 29,
        size: 14,
        font: bold,
        color: white,
      });
      if (subtitle) {
        page.drawText(subtitle, {
          x: MARGIN,
          y: PAGE_H - 45,
          size: 8.5,
          font: regular,
          color: white,
        });
      }
    };
    // Summary sections use one outer container, with unboxed fields inside.
    // Document entries remain individual bordered rows.
    const drawSectionFrame = (page, title, x, topY, width, height) => {
      page.drawRectangle({
        x,
        y: topY - height,
        width,
        height,
        color: white,
        borderColor: border,
        borderWidth: 0.8,
      });
      page.drawText(title.toUpperCase(), {
        x: x + 12,
        y: topY - 19,
        size: 9,
        font: bold,
        color: navy,
      });
    };

    const drawPlainField = (page, label, value, x, y, maxWidth) => {
      page.drawText(String(label || "").toUpperCase(), {
        x,
        y,
        size: 6.5,
        font: bold,
        color: muted,
      });
      page.drawText(String(value || "-"), {
        x,
        y: y - 15,
        size: 9.5,
        font: bold,
        color: ink,
        maxWidth,
      });
    };

    const drawDocumentRow = (page, label, value, x, y, width, height = 38) => {
      page.drawRectangle({
        x,
        y: y - height,
        width,
        height,
        color: white,
        borderColor: border,
        borderWidth: 0.7,
      });
      page.drawText(String(label || "").toUpperCase(), {
        x: x + 10,
        y: y - 14,
        size: 6.5,
        font: bold,
        color: muted,
      });
      page.drawText(String(value || "-"), {
        x: x + 10,
        y: y - 27,
        size: 9.5,
        font: bold,
        color: ink,
        maxWidth: width - 20,
      });
    };

    const cover = pdfDoc.addPage([PAGE_W, PAGE_H]);
    drawHeader(
      cover,
      viewRow?.vehicle_registration_number || "Vehicle Documents",
      `${viewRow?.vehicle_display_number || "-"} · ${[viewRow?.make_name, viewRow?.model_name].filter(Boolean).join(" ") || "-"}`
    );

    const contentW = PAGE_W - MARGIN * 2;
    const fieldW = (contentW - 34) / 2;

    // VEHICLE: a single outer box, not separate boxes around each value.
    const vehicleTop = PAGE_H - 88;
    const vehicleHeight = 126;
    drawSectionFrame(cover, "Vehicle", MARGIN, vehicleTop, contentW, vehicleHeight);

    const vehicleFields = [
      ["Service Location", viewRow?.service_location_name],
      ["Branch", viewRow?.branch_name],
      ["Company", viewRow?.company_name],
      ["Make", viewRow?.make_name],
      ["Model", viewRow?.model_name],
    ];

    vehicleFields.forEach(([label, value], index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = MARGIN + 16 + col * (fieldW + 18);
      const fieldY = vehicleTop - 39 - row * 29;
      drawPlainField(cover, label, value, x, fieldY, fieldW);
    });

    let y = vehicleTop - vehicleHeight - 28;
    cover.drawText("DOCUMENTS", {
      x: MARGIN,
      y,
      size: 9,
      font: bold,
      color: navy,
    });
    y -= 12;

    DOC_KEYS.forEach((key) => {
      const has = !!viewDoc?.[`${key}_file_url`];
      const expiry = viewDoc?.[`${key}_expiry_date`];

      if (y - 38 < 48) {
        const next = pdfDoc.addPage([PAGE_W, PAGE_H]);
        drawHeader(next, viewRow?.vehicle_registration_number || "Vehicle Documents", "Documents");
        y = PAGE_H - 92;
      }

      drawDocumentRow(
        cover,
        DOC_LABELS[key],
        has ? formatDate(expiry) : "Not uploaded",
        MARGIN,
        y,
        contentW,
        38
      );
      y -= 46;
    });

    // AUDIT: same single-container treatment as Vehicle.
    const audit = pdfDoc.addPage([PAGE_W, PAGE_H]);
    drawHeader(audit, viewRow?.vehicle_registration_number || "Vehicle Documents", "Audit");

    const auditTop = PAGE_H - 88;
    const auditHeight = viewDoc?.is_deleted ? 112 : 84;
    drawSectionFrame(audit, "Audit", MARGIN, auditTop, contentW, auditHeight);

    const auditFields = [
      ["Created By", resolveUserName(viewDoc?.created_by_name || viewDoc?.created_by)],
      ["Updated By", resolveUserName(viewDoc?.updated_by_name || viewDoc?.updated_by)],
      ["Created At", formatServerDateTime(viewDoc?.created_at)],
      ["Updated At", formatServerDateTime(viewDoc?.updated_at)],
      ...(viewDoc?.is_deleted
        ? [
            ["Deleted By", resolveUserName(viewDoc?.deleted_by_name || viewDoc?.deleted_by)],
            ["Deleted At", formatServerDateTime(viewDoc?.deleted_at)],
          ]
        : []),
    ];

    auditFields.forEach(([label, value], index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = MARGIN + 16 + col * (fieldW + 18);
      const fieldY = auditTop - 39 - row * 29;
      drawPlainField(audit, label, value, x, fieldY, fieldW);
    });

    // Add each uploaded document sequentially after the summary.
    for (const key of DOC_KEYS) {
      const fileUrl = viewDoc?.[`${key}_file_url`];
      if (!fileUrl) continue;
      const resolvedUrl = vehicleDocumentsService.resolveFileUrl(fileUrl);
      try {
        const { blob } = await vehicleDocumentsService.downloadFile(fileUrl);
        const contentType = (blob.type || "").toLowerCase();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const looksLikeImage =
          contentType.startsWith("image/") ||
          (!contentType && isImageFile(resolvedUrl));
        const looksLikePdf =
          contentType.includes("pdf") ||
          (!contentType && isPdfFile(resolvedUrl));
        if (looksLikeImage) {
          const page = pdfDoc.addPage([PAGE_W, PAGE_H]);
          drawHeader(page, DOC_LABELS[key], viewRow?.vehicle_registration_number || "Vehicle");
          const pngBytes = await rasterizeImageToPng(bytes);
          const image = await pdfDoc.embedPng(pngBytes);
          const areaX = MARGIN;
          const areaY = MARGIN;
          const areaW = PAGE_W - MARGIN * 2;
          const areaH = PAGE_H - 115;
          const scale = Math.min(areaW / image.width, areaH / image.height, 1);
          const w = image.width * scale;
          const h = image.height * scale;
          page.drawRectangle({
            x: areaX,
            y: areaY,
            width: areaW,
            height: areaH,
            color: white,
            borderColor: border,
            borderWidth: 0.8,
          });
          page.drawImage(image, {
            x: areaX + (areaW - w) / 2,
            y: areaY + (areaH - h) / 2,
            width: w,
            height: h,
          });
        } else if (looksLikePdf) {
          const srcDoc = await PDFDocument.load(bytes);
          const copied = await pdfDoc.copyPages(srcDoc, srcDoc.getPageIndices());
          copied.forEach((p) => pdfDoc.addPage(p));
        } else {
          const page = pdfDoc.addPage([PAGE_W, PAGE_H]);
          drawHeader(page, DOC_LABELS[key], viewRow?.vehicle_registration_number || "Vehicle");
          page.drawText("This file type is not previewable here.", {
            x: MARGIN,
            y: PAGE_H - 115,
            size: 11,
            font: regular,
            color: ink,
          });
        }
      } catch (err) {
        const page = pdfDoc.addPage([PAGE_W, PAGE_H]);
        drawHeader(page, DOC_LABELS[key], viewRow?.vehicle_registration_number || "Vehicle");
        page.drawText("Document could not be loaded for this export.", {
          x: MARGIN,
          y: PAGE_H - 115,
          size: 11,
          font: regular,
          color: ink,
        });
        page.drawText(String(err?.message || err), {
          x: MARGIN,
          y: PAGE_H - 135,
          size: 8.5,
          font: regular,
          color: muted,
          maxWidth: PAGE_W - MARGIN * 2,
        });
      }
    }
    return pdfDoc.save();
  };
  const handleDownloadAll = async () => {
    if (!viewDoc || downloadingAll) return;
    setDownloadingAll(true);
    setViewError("");
    try {
      const pdfBytes = await buildCombinedPdf();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${viewRow?.vehicle_registration_number || "vehicle"}-documents.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error("Combined PDF failed:", err);
      setViewError("Couldn't generate the combined PDF. Please try downloading documents individually.");
    } finally {
      setDownloadingAll(false);
    }
  };
  const escapePrintHtml = (value) =>
    String(value ?? "-")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  const handlePrint = async () => {
    if (!viewRow || !viewDoc) return;
    const printWindow = window.open("about:blank", "_blank", "width=1100,height=900");
    if (!printWindow) {
      setViewError("Please allow pop-ups for printing.");
      return;
    }
    try {
      setViewError("");
      // Use the exact same PDF builder as Download.
      // This is important because it includes BOTH image files and real PDF files.
      const pdfBytes = await buildCombinedPdf();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      printWindow.document.title = `${viewRow.vehicle_registration_number || "Vehicle"} - Print`;
      printWindow.location.href = blobUrl;
      // Let the browser PDF viewer finish loading before invoking print.
      let printStarted = false;
      const startPrint = () => {
        if (printStarted) return;
        printStarted = true;
        try {
          printWindow.focus();
          printWindow.print();
        } catch (err) {
          console.error("PDF print failed:", err);
        }
      };
      const printAfterLoad = () => {
        setTimeout(startPrint, 900);
      };
      try {
        printWindow.addEventListener("load", printAfterLoad, { once: true });
      } catch {
        setTimeout(printAfterLoad, 1800);
      }
      // Fallback for Chromium PDF viewer where the popup load event is not exposed.
      setTimeout(startPrint, 2200);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error("Print PDF failed:", err);
      try {
        printWindow.close();
      } catch { }
      setViewError("Couldn't prepare the complete print PDF. Please try again.");
    }
  };
  const expiryBadge = (row) => {
    if (row.expired_count > 0) {
      return <span className="vdl-badge vdl-badge--expired"><FiAlertTriangle size={11} /> {row.expired_count} expired</span>;
    }
    if (row.expiring_soon_count > 0) {
      return <span className="vdl-badge vdl-badge--soon"><FiClock size={11} /> {row.expiring_soon_count} expiring soon</span>;
    }
    return <span className="vdl-badge vdl-badge--ok">Up to date</span>;
  };
  if (!hasDocumentAccess) {
    return (
      <div className="vdl-shell">
        <Sidebar />
        <div className="vdl-main">
          <Header />
          <div className="vdl-page vdl-clean-reference">
            <div className="vdl-error">
              <FiAlertCircle size={16} />
              <span>You don't have permission to view vehicle documents. Contact your admin if you need access.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="vdl-shell">
      <Sidebar />
      <div className="vdl-main">
        <Header />
        <div className="vdl-page vdl-clean-reference">
          <div className="vdl-stats">
            <button
              type="button"
              className={`vdl-stat vdl-stat--total ${statusFilter === "all" ? "is-selected" : ""
                }`}
              onClick={() => handleStatusCardClick("all")}
              aria-pressed={statusFilter === "all"}
            >
              <span className="vdl-stat-icon">
                <FiTruck />
              </span>
              <span className="vdl-stat-content">
                <span className="vdl-stat-val">{stats.total}</span>
                <span className="vdl-stat-lbl">Total Vehicles</span>
              </span>
            </button>
            <button
              type="button"
              className={`vdl-stat vdl-stat--expired ${statusFilter === "expired" ? "is-selected" : ""
                }`}
              onClick={() => handleStatusCardClick("expired")}
              aria-pressed={statusFilter === "expired"}
            >
              <span className="vdl-stat-icon">
                <FiAlertTriangle />
              </span>
              <span className="vdl-stat-content">
                <span className="vdl-stat-val">{stats.withExpired}</span>
                <span className="vdl-stat-lbl">With Expired Docs</span>
              </span>
            </button>
            <button
              type="button"
              className={`vdl-stat vdl-stat--soon ${statusFilter === "soon" ? "is-selected" : ""
                }`}
              onClick={() => handleStatusCardClick("soon")}
              aria-pressed={statusFilter === "soon"}
            >
              <span className="vdl-stat-icon">
                <FiClock />
              </span>
              <span className="vdl-stat-content">
                <span className="vdl-stat-val">{stats.withExpiringSoon}</span>
                <span className="vdl-stat-lbl">Expiring Soon</span>
              </span>
            </button>
            <button
              type="button"
              className={`vdl-stat vdl-stat--clear ${statusFilter === "clear" ? "is-selected" : ""
                }`}
              onClick={() => handleStatusCardClick("clear")}
              aria-pressed={statusFilter === "clear"}
            >
              <span className="vdl-stat-icon">
                <FiFileText />
              </span>
              <span className="vdl-stat-content">
                <span className="vdl-stat-val">{stats.clean}</span>
                <span className="vdl-stat-lbl">All Clear</span>
              </span>
            </button>
          </div>
          {statusFilter !== "all" && (
            <div className="vdl-active-filter">
              <span>
                Showing{" "}
                <strong>
                  {statusFilter === "expired"
                    ? "Expired Documents"
                    : statusFilter === "soon"
                      ? "Expiring Soon"
                      : "All Clear"}
                </strong>{" "}
                · {filteredRows.length} vehicle{filteredRows.length === 1 ? "" : "s"}
              </span>
              <button type="button" onClick={() => handleStatusCardClick("all")}>
                Clear filter
              </button>
            </div>
          )}
          <div className="vdl-toolbar">
            <div className="vdl-search" style={{ flex: "0 1 320px", maxWidth: 320 }}>
              <FiSearch size={14} />
              <input
                type="text"
                placeholder="Search by registration number or display code…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="vdl-clear" onClick={() => setSearchTerm("")} aria-label="Clear">
                  <FiX size={12} />
                </button>
              )}
            </div>
            {hasDocumentAccess && (
              <button className="vdl-add-btn" onClick={goToAddPage}>
                <FiPlus size={15} /> Add Vehicle Document
              </button>
            )}
          </div>
          {errorMsg && (
            <div className="vdl-error">
              <FiAlertCircle size={16} />
              <span>{errorMsg}</span>
              <button onClick={() => loadRows(searchTerm || undefined)}><FiRefreshCw size={13} /> Retry</button>
            </div>
          )}
          <div className="vdl-table-card">
            <div className="vdl-table-wrap">
              <table className="vdl-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Location</th>
                    <th>Branch</th>
                    <th>Company</th>
                    <th>Make / Model</th>
                    <th>Nearest Expiry</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    {(canViewDocuments || canEditDocuments || canDeleteDocuments) && <th className="col-actions">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={canViewDocuments || canEditDocuments || canDeleteDocuments ? 9 : 8} className="vdl-state">
                      <div className="vdl-loading"><span className="vdl-spinner" /> Loading vehicle documents…</div>
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={canViewDocuments || canEditDocuments || canDeleteDocuments ? 9 : 8} className="vdl-state">
                      <FiTruck size={28} style={{ opacity: 0.3 }} />
                      <p>No vehicle documents found.</p>
                      {canEditDocuments && (
                        <button className="vdl-link-btn" onClick={goToAddPage}>Add your first vehicle document</button>
                      )}
                    </td></tr>
                  ) : (
                    pagedRows.map((r) => (
                      <tr key={r.id} className={`vdl-row-clickable ${r.is_deleted ? "row-inactive" : ""}`} onClick={() => { if (canViewDocuments) openView(r); }}>
                        <td className="cell-name" data-label="Vehicle">
                          {r.vehicle_registration_number}
                          {r.vehicle_display_number && <span className="vdl-sub">{r.vehicle_display_number}</span>}
                          {r.is_deleted && (
                            <span className="vdl-badge vdl-badge--expired" style={{ marginLeft: 8 }}>Deleted</span>
                          )}
                        </td>
                        <td data-label="Location">{r.service_location_name || "-"}</td>
                        <td data-label="Branch">{r.branch_name || "-"}</td>
                        <td data-label="Company">{r.company_name || "-"}</td>
                        <td data-label="Make / Model">{[r.make_name, r.model_name].filter(Boolean).join(" / ") || "-"}</td>
                        <td data-label="Nearest Expiry">{formatDate(r.nearest_expiry_date)}</td>
                        <td data-label="Status">{expiryBadge(r)}</td>
                        <td data-label="Last Updated">{formatServerDateTime(r.updated_at)}</td>
                        {(canViewDocuments || canEditDocuments || canDeleteDocuments) && (
                          <td data-label="Actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`vdl-actions-btn ${openActionRowId === r.id ? "vdl-actions-btn--active" : ""}`}
                              ref={(el) => { actionButtonRefs.current[r.id] = el; }}
                              onClick={() => openActionsMenu(r.id)}
                            >
                              Actions
                              <FiChevronDown size={13} />
                            </button>
                            {openActionRowId === r.id && createPortal(
                              <div
                                className="vdl-actions-menu"
                                ref={actionMenuRef}
                                style={{ top: menuPos.top, left: menuPos.left }}
                              >
                                {canViewDocuments && (
                                  <button
                                    type="button"
                                    className="vdl-actions-menu-item"
                                    onClick={() => {
                                      setOpenActionRowId(null);
                                      openView(r);
                                    }}
                                  >
                                    View
                                  </button>
                                )}
                                {canEditDocuments && (
                                  <button
                                    type="button"
                                    className="vdl-actions-menu-item"
                                    onClick={() => {
                                      setOpenActionRowId(null);
                                      goToEditPage(r);
                                    }}
                                  >
                                    <FiEdit2 size={13} /> Edit
                                  </button>
                                )}
                                {canDeleteDocuments && (
                                  <button
                                    type="button"
                                    className="vdl-actions-menu-item delete"
                                    disabled={deletingId === r.id}
                                    onClick={(e) => {
                                      setOpenActionRowId(null);
                                      handleDeleteRow(r, e);
                                    }}
                                  >
                                    <FiTrash2 size={13} /> {deletingId === r.id ? "Deleting…" : "Delete"}
                                  </button>
                                )}
                              </div>,
                              document.body
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && rows.length > 0 && (
              <div className="vdl-table-footer">
                <span>
                  Showing <strong>{pageStart}</strong>–<strong>{pageEnd}</strong> of <strong>{filteredRows.length}</strong> vehicles
                </span>
                <div className="vdl-pagination">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  >
                    ‹
                  </button>
                  <span>Page {page} of {totalPages}</span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
          {viewRow && (
            <div className="vdl-modal-overlay" onClick={closeView}>
              <div className="vdl-modal" onClick={(e) => e.stopPropagation()}>
                <div className="vdl-modal-header">
                  <div>
                    <h3>{viewRow.vehicle_registration_number}</h3>
                    <p>{viewRow.vehicle_display_number || "-"} &nbsp;·&nbsp; {viewRow.make_name} {viewRow.model_name}</p>
                  </div>
                  <div className="vdl-modal-header-actions">
                    <button
                      type="button"
                      className="vdl-print-btn"
                      onClick={handlePrint}
                    >
                      <FiPrinter size={13} /> Print
                    </button>
                    <button
                      type="button"
                      className="vdl-download-all-btn"
                      onClick={handleDownloadAll}
                      disabled={!viewDoc || downloadingAll}
                    >
                      <FiDownload size={13} />
                      {downloadingAll ? "Building PDF…" : "Download"}
                    </button>
                    <button className="vdl-modal-close" onClick={closeView} aria-label="Close"><FiX size={18} /></button>
                  </div>
                </div>
                <div className="vdl-modal-body vdl-print-area">
                  {viewLoading && (
                    <div className="vdl-view-loading"><span className="vdl-spinner" /> Loading full details…</div>
                  )}
                  {viewError && (
                    <div className="vdl-error" style={{ marginBottom: 16 }}>
                      <FiAlertCircle size={16} /><span>{viewError}</span>
                    </div>
                  )}
                  <div className="vdl-view-section">
                    <h4>Vehicle</h4>
                    <div className="vdl-view-grid">
                      <div className="vdl-view-field"><span>Service Location</span><strong>{viewRow.service_location_name || "-"}</strong></div>
                      <div className="vdl-view-field"><span>Branch</span><strong>{viewRow.branch_name || "-"}</strong></div>
                      <div className="vdl-view-field"><span>Company</span><strong>{viewRow.company_name || "-"}</strong></div>
                      <div className="vdl-view-field"><span>Make</span><strong>{viewRow.make_name || "-"}</strong></div>
                      <div className="vdl-view-field"><span>Model</span><strong>{viewRow.model_name || "-"}</strong></div>
                    </div>
                  </div>
                  <div className="vdl-view-section">
                    <h4>Documents</h4>
                    <div className="vdl-view-doc-list">
                      {DOC_KEYS.map((key) => {
                        const expiry = viewDoc?.[`${key}_expiry_date`];
                        const fileUrl = viewDoc?.[`${key}_file_url`];
                        const resolvedUrl = fileUrl ? vehicleDocumentsService.resolveFileUrl(fileUrl) : null;
                        const isExpired = expiry && new Date(expiry) < new Date();
                        const isImg = resolvedUrl && isImageFile(resolvedUrl);
                        const isDownloading = downloadingKey === key;
                        return (
                          <div className="vdl-view-doc-row" key={key}>
                            <div className="vdl-view-doc-thumb-wrap">
                              {resolvedUrl ? (
                                isImg ? (
                                  <img
                                    src={thumbUrls[key] || ""}
                                    alt={DOC_LABELS[key]}
                                    className="vdl-view-doc-thumb"
                                    draggable={false}
                                    onContextMenu={(e) => e.preventDefault()}
                                    onClick={() => openDocPreview(fileUrl, DOC_LABELS[key])}
                                  />
                                ) : (
                                  <div
                                    className="vdl-view-doc-thumb vdl-view-doc-thumb--icon"
                                    onClick={() => openDocPreview(fileUrl, DOC_LABELS[key])}
                                  >
                                    <FiFileText size={16} />
                                  </div>
                                )
                              ) : (
                                <div className="vdl-view-doc-thumb vdl-view-doc-thumb--icon">
                                  <FiFileText size={16} />
                                </div>
                              )}
                            </div>
                            <span className="vdl-view-doc-name">{DOC_LABELS[key]}</span>
                            <span className={`vdl-view-doc-expiry ${isExpired ? "is-expired" : ""}`}>
                              {formatDate(expiry)}
                            </span>
                            {fileUrl ? (
                              <div className="vdl-view-doc-actions">
                                <button
                                  type="button"
                                  className="vdl-view-doc-download"
                                  disabled={isDownloading}
                                  onClick={() => {
                                    const ext = (fileUrl.match(/(\.[a-zA-Z0-9]+)(?:\?.*)?$/) || [""])[0].split("?")[0] || "";
                                    handleDownload(
                                      fileUrl,
                                      `${viewRow?.vehicle_registration_number || "vehicle"}-${DOC_LABELS[key]}${ext}`,
                                      key
                                    );
                                  }}
                                >
                                  <FiDownload size={12} /> {isDownloading ? "Downloading…" : "Download"}
                                </button>
                              </div>
                            ) : (
                              <em>Not uploaded</em>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="vdl-view-section">
                    <h4>Audit</h4>
                    <div className="vdl-view-grid">
                      <div className="vdl-view-field">
                        <span>Created By</span>
                        <strong>{resolveUserName(viewDoc?.created_by_name || viewDoc?.created_by)}</strong>
                      </div>
                      <div className="vdl-view-field">
                        <span>Updated By</span>
                        <strong>{resolveUserName(viewDoc?.updated_by_name || viewDoc?.updated_by)}</strong>
                      </div>
                      <div className="vdl-view-field"><span>Created At</span><strong>{formatServerDateTime(viewDoc?.created_at)}</strong></div>
                      <div className="vdl-view-field"><span>Updated At</span><strong>{formatServerDateTime(viewDoc?.updated_at)}</strong></div>
                      {viewDoc?.is_deleted && (
                        <>
                          <div className="vdl-view-field">
                            <span>Deleted By</span>
                            <strong>{resolveUserName(viewDoc?.deleted_by_name || viewDoc?.deleted_by)}</strong>
                          </div>
                          <div className="vdl-view-field"><span>Deleted At</span><strong>{formatServerDateTime(viewDoc?.deleted_at)}</strong></div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(previewDoc || previewLoading || previewError) && (
            <div className="vdl-modal-overlay" onClick={closeDocPreview}>
              <div
                className="vdl-modal vdl-doc-preview-modal"
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className="vdl-modal-header">
                  <h3>{previewDoc?.label || "Document Preview"}</h3>
                  <button className="vdl-modal-close" onClick={closeDocPreview} aria-label="Close"><FiX size={18} /></button>
                </div>
                <div className="vdl-modal-body vdl-doc-preview-body">
                  {previewLoading && (
                    <div className="vdl-view-loading">
                      <span className="vdl-spinner" /> Loading document…
                    </div>
                  )}
                  {previewError && !previewLoading && (
                    <div className="vdl-error">
                      <FiAlertCircle size={16} />
                      <span>{previewError}</span>
                    </div>
                  )}
                  {!previewLoading && !previewError && previewDoc?.kind === "image" && (
                    <img
                      src={previewDoc.url}
                      alt={previewDoc.label}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        userSelect: "none",
                        display: "block",
                        margin: "0 auto",
                      }}
                    />
                  )}
                  {!previewLoading && !previewError && previewDoc?.kind === "pdf" && (
                    <iframe
                      src={`${previewDoc.url}#toolbar=0`}
                      title={previewDoc.label}
                      style={{ width: "100%", height: "70vh", border: "none" }}
                    />
                  )}
                  {!previewLoading && !previewError && previewDoc?.kind === "other" && (
                    <div className="vdl-doc-preview-fallback">
                      <FiFileText size={32} style={{ opacity: 0.4 }} />
                      <p>Preview isn't available for this file type.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {toast && (
            <div
              className="vdl-toast-overlay"
              style={{
                position: "fixed",
                bottom: "28px",
                right: "28px",
                zIndex: 3000,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "14px 18px",
                borderRadius: "10px",
                minWidth: "260px",
                maxWidth: "380px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                background: toast.type === "success" ? "#16a34a" : "#dc2626",
                animation: "vdlToastSlideIn 0.25s ease-out",
              }}
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>
                {toast.type === "success" ? "✓" : "⚠"}
              </span>
              <span style={{ flex: 1 }}>{toast.text}</span>
              <button
                type="button"
                onClick={dismissToast}
                aria-label="Dismiss"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  opacity: 0.85,
                  cursor: "pointer",
                  fontSize: "15px",
                  lineHeight: 1,
                  padding: "2px",
                }}
              >
                ✕
              </button>
            </div>
          )}
          <style>{`
        @keyframes vdlToastSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
      </div>
    </div>
  );
}
